import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@parcel/ui';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  type LucideIcon,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';
import * as React from 'react';
import { DetailIdentitySkeleton } from './detail-skeletons.js';

export type DetailMode = 'drawer' | 'expanded';

export function shellDismissIntent(mode: DetailMode): 'close' | 'collapse' {
  return mode === 'expanded' ? 'collapse' : 'close';
}

interface EntityDrawerShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: DetailMode;
  onCollapse?: () => void;
  children: React.ReactNode;
}

export function EntityDrawerShell({
  open,
  onOpenChange,
  mode = 'drawer',
  onCollapse,
  children,
}: EntityDrawerShellProps) {
  const expanded = mode === 'expanded';
  // Esc and backdrop clicks step down one level: expanded collapses, drawer closes.
  const interceptDismiss = (event: Event) => {
    if (shellDismissIntent(mode) !== 'collapse' || !onCollapse) return;
    event.preventDefault();
    onCollapse();
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-mode={mode}
        onEscapeKeyDown={interceptDismiss}
        onPointerDownOutside={interceptDismiss}
        overlayClassName={cn(
          // Drawer dims only; expanded adds a blur so the panel reads as a page on top.
          'bg-black/30 transition-[background-color,backdrop-filter] duration-300 motion-reduce:transition-none',
          expanded && 'md:bg-black/50 md:backdrop-blur-sm',
        )}
        // Inline so the mode transition is 250ms without touching the open/close animation.
        style={{ transitionDuration: '250ms' }}
        className={cn(
          'flex h-auto w-full flex-col gap-0 overflow-hidden p-0 sm:w-[48rem] sm:max-w-[100vw]',
          'transition-[top,right,bottom,width,border-radius] ease-out motion-reduce:transition-none',
          expanded &&
            'md:bottom-4 md:right-4 md:top-4 md:w-[calc(100vw-2rem)] md:rounded-xl md:border',
          // Hide the default Radix close button rendered by SheetContent.
          '[&>button.absolute]:hidden',
        )}
      >
        {children}
      </SheetContent>
    </Sheet>
  );
}

/** Which part of the header to draw; the expanded layout places the bar and the identity apart. */
export const HeaderPartContext = React.createContext<'all' | 'bar' | 'identity'>('all');

interface EntityDrawerHeaderProps {
  eyebrow: string;
  title: string;
  metaIcon: LucideIcon;
  metaText: string;
  /** Optional custom meta row, rendered in place of the metaIcon + metaText line. */
  metaSlot?: React.ReactNode;
  /** Status and type badges under the meta line. */
  chips?: React.ReactNode;
  entityIcon: React.ReactNode;
  /** Optional action rendered at the right edge of the title row (e.g. an "Open project" button). */
  action?: React.ReactNode;
  /** A worded action (Follow) that leads the row, ahead of the icon links and buttons. */
  leadingAction?: React.ReactNode;
  externalUrl?: string;
  externalLabel?: string;
  /** The external link's icon; a plain link glyph by default. */
  externalIcon?: React.ReactNode;
  /** More external links after the first, each an icon button (a firm's LinkedIn page). */
  moreExternalLinks?: Array<{ url: string; label: string; icon: React.ReactNode }>;
  onPrev?: () => void;
  onNext?: () => void;
  mode?: DetailMode;
  /** Renders the expand/collapse button when provided. */
  onToggleMode?: () => void;
  backLabel?: string;
  onBack?: () => void;
  /** Draws the identity as a skeleton; the title stays for screen readers. */
  loading?: boolean;
}

export function EntityDrawerHeader({
  eyebrow,
  title,
  metaIcon: MetaIcon,
  metaText,
  metaSlot,
  chips,
  entityIcon,
  action,
  leadingAction,
  externalUrl,
  externalLabel,
  externalIcon,
  moreExternalLinks = [],
  onPrev,
  onNext,
  mode = 'drawer',
  onToggleMode,
  backLabel,
  onBack,
  loading = false,
}: EntityDrawerHeaderProps) {
  const part = React.useContext(HeaderPartContext);
  const bar = (
    <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b px-6">
      <div className="flex min-w-0 items-center gap-2">
        {backLabel && onBack ? (
          <button
            type="button"
            aria-label={`Back to ${backLabel}`}
            onClick={onBack}
            className="inline-flex h-7 min-w-0 max-w-[16rem] items-center gap-1.5 rounded-md px-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <span className="truncate">{backLabel}</span>
          </button>
        ) : (
          <span className="text-sm font-medium text-muted-foreground">{eyebrow}</span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        <IconButton label="Previous" icon={ChevronUp} onClick={onPrev} disabled={!onPrev} />
        <IconButton label="Next" icon={ChevronDown} onClick={onNext} disabled={!onNext} />
        {onToggleMode ? (
          <IconButton
            label={mode === 'expanded' ? 'Collapse' : 'Expand'}
            icon={mode === 'expanded' ? Minimize2 : Maximize2}
            onClick={onToggleMode}
            className="hidden md:inline-flex"
          />
        ) : null}
        <SheetClose asChild>
          <IconButton label="Close" icon={X} />
        </SheetClose>
      </div>
    </div>
  );
  if (part === 'bar') return bar;
  const identity = loading ? (
    <div className="flex shrink-0 px-6 py-4">
      <SheetTitle className="sr-only">{title}</SheetTitle>
      <SheetDescription className="sr-only">{metaText}</SheetDescription>
      <DetailIdentitySkeleton />
    </div>
  ) : (
    <div className="flex shrink-0 items-start gap-4 px-6 py-4">
      {entityIcon}
      <div className="min-w-0 flex-1">
        <SheetTitle asChild>
          <h2 className="truncate text-xl font-semibold leading-tight text-foreground">{title}</h2>
        </SheetTitle>
        <SheetDescription className="sr-only">{metaText}</SheetDescription>
        {metaSlot ?? (
          <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <MetaIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{metaText}</span>
          </div>
        )}
        {chips ? <div className="mt-2 flex flex-wrap items-center gap-1.5">{chips}</div> : null}
      </div>
      {leadingAction || action || externalUrl || moreExternalLinks.length > 0 ? (
        <div className="flex shrink-0 items-center gap-1">
          {leadingAction}
          {[
            ...(externalUrl
              ? [
                  {
                    url: externalUrl,
                    label: externalLabel ?? 'Open external link',
                    icon: externalIcon ?? <LinkIcon className="h-4 w-4" />,
                  },
                ]
              : []),
            ...moreExternalLinks,
          ].map((link) => (
            <TooltipProvider key={link.url}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={link.label}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {link.icon}
                  </a>
                </TooltipTrigger>
                <TooltipContent>{link.label}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
          {/* Links first (LinkedIn, then the site), then the record's own action. */}
          {action}
        </div>
      ) : null}
    </div>
  );
  if (part === 'identity') return identity;
  return (
    <>
      {bar}
      {identity}
    </>
  );
}

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon: LucideIcon;
}

const IconButton = ({ label, icon: Icon, className, ...props }: IconButtonProps) => (
  <button
    type="button"
    aria-label={label}
    className={cn(
      'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
      className,
    )}
    {...props}
  >
    <Icon className="h-4 w-4" />
  </button>
);

interface EntityAvatarProps {
  src?: string | null;
  alt: string;
  fallbackIcon: LucideIcon;
  size?: 'xs' | 'sm' | 'md';
  href?: string | null;
  hrefLabel?: string;
}

export function EntityAvatar({
  src,
  alt,
  fallbackIcon: Icon,
  size = 'md',
  href,
  hrefLabel,
}: EntityAvatarProps) {
  const [erroredSrc, setErroredSrc] = React.useState<string | null>(null);
  const showImage = Boolean(src) && src !== erroredSrc;
  const containerSize = size === 'xs' ? 'h-5 w-5' : size === 'sm' ? 'h-8 w-8' : 'h-12 w-12';
  const iconSize = size === 'md' ? 'h-5 w-5' : size === 'sm' ? 'h-4 w-4' : 'h-3 w-3';
  const className = cn(
    'flex shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted',
    href &&
      'cursor-pointer transition hover:scale-105 hover:bg-muted/80 focus-visible:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    containerSize,
  );
  const content =
    showImage && src ? (
      <img
        src={src}
        alt={alt}
        className="h-full w-full object-contain"
        onError={() => setErroredSrc(src)}
      />
    ) : (
      <Icon className={cn(iconSize, 'text-muted-foreground')} />
    );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={hrefLabel ?? `Open ${alt}`}
        className={className}
        onClick={(event) => event.stopPropagation()}
      >
        {content}
      </a>
    );
  }

  return <div className={className}>{content}</div>;
}
